// M31 — Friendly fallback for when a React subtree throws and the
// Sentry ErrorBoundary catches it. Three things this page needs to do:
//
// 1. Tell the user *something* went wrong without being scary
// 2. Surface a way to recover (reload — usually clears the bad state)
// 3. Surface a way to escape (back to dashboard) for when reload won't help
//
// M32 — Sentry has already captured the error by the time this renders,
// but the stack trace doesn't tell us what the user was *trying* to do.
// The "Tell us what happened" button opens the feedback dialog with
// kind=bug pre-selected and the error name + stack baked into the
// `error_context` column so the maintainer can pair the report with
// the Sentry event.

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined';
import QSLogo from './QSLogo';
import { useFeedback } from './useFeedback';
import { formatErrorContext } from '../lib/feedback';

export interface ErrorFallbackProps {
  resetError?: () => void;
  /** The thrown value caught by Sentry.ErrorBoundary. Used to seed the
   * feedback dialog's `error_context` field when the user opts to send
   * a note about what they were doing when it broke. */
  error?: unknown;
}

export default function ErrorFallback({ resetError, error }: ErrorFallbackProps) {
  const { open: openFeedback } = useFeedback();

  function handleReload() {
    // Try the Sentry ErrorBoundary's reset first — if it works, the
    // user stays on the same route. If the error keeps recurring, a
    // full page reload is the next escape valve.
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  }

  function handleGoHome() {
    // Full reload + path swap. Wipes any bad in-memory state.
    window.location.href = '/';
  }

  function handleSendFeedback() {
    openFeedback({
      kind: 'bug',
      prefillMessage: 'I was trying to … (please describe)\n',
      errorContext: error ? formatErrorContext(error) : undefined,
    });
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default', px: 2 }}>
      <Card sx={{ maxWidth: 460, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <QSLogo variant="icon" height={48} />
            <ErrorOutlineOutlined sx={{ fontSize: 56, color: 'error.main', mt: 2 }} />
          </Box>

          <Typography variant="h5" align="center" sx={{ mb: 1.5, fontWeight: 600 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            QuarterStack hit an error and the page can't continue. The
            problem's been reported automatically — you don't need to do
            anything. Try one of the options below.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="contained" fullWidth onClick={handleReload}>
              Try again
            </Button>
            <Button variant="outlined" fullWidth onClick={handleGoHome}>
              Go to dashboard
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={handleSendFeedback}
              startIcon={<FeedbackOutlined />}
            >
              Tell us what happened
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
